import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';
import { AnalyticsPayload, AnalyticsState } from '../../interfaces/interface';
import extraReducers from '../../extraReducers/analyticsExtraReducer';

const initialState: AnalyticsState = {
  analytics: {
    data: {
      total_tasks_completed: 0,
      total_tasks_added: 0,
      total_tasks_allocated: 0,
      total_reviews_allocated: 0,
      total_reviews_completed: 0
    },
    message: "",
    status: undefined
  },
  error: null,
  getStatus: STATUS.NOT_STARTED,
  getAnalyticsMessage: ""
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toPercentage = (numerator: number, denominator: number): number => {
  if (denominator <= 0) {
    return 0;
  }
  return Number(((numerator / denominator) * 100).toFixed(2));
};

const normalizeOverviewMetrics = (raw: any) => {
  const source = raw?.metrics || raw;
  const acceptedAsIs = toNumber(
    source?.quality_accepted_as_is_tasks ??
    source?.accepted_as_is_tasks ??
    source?.accepted_as_is_count ??
    0
  );
  const acceptedWithEdits = toNumber(
    source?.quality_accepted_with_edits_tasks ??
    source?.accepted_with_edits_tasks ??
    source?.accepted_with_edits_count ??
    0
  );

  const acceptedTotal = toNumber(
    source?.quality_accepted_tasks ??
    source?.accepted_count ??
    acceptedAsIs + acceptedWithEdits
  );

  // Prefer cumulative rejected count when backend provides it so rework does not reduce historic rejections.
  const rejectedCount = toNumber(
    source?.quality_rejected_total_tasks ??
    source?.total_quality_rejected ??
    source?.quality_rejected_tasks ??
    source?.rejected_count ??
    0
  );

  const reviewedTotal = toNumber(source?.total_reviewed ?? acceptedTotal + rejectedCount);

  const acceptedAsIsPercentage = toNumber(
    source?.quality_accepted_as_is_percentage ??
    source?.accepted_as_is_percentage ??
    toPercentage(acceptedAsIs, reviewedTotal)
  );

  const acceptedWithEditsPercentage = toNumber(
    source?.quality_accepted_with_edits_percentage ??
    source?.accepted_with_edits_percentage ??
    toPercentage(acceptedWithEdits, reviewedTotal)
  );

  const rejectedPercentage = toNumber(
    source?.quality_rejected_percentage ??
    source?.rejected_percentage ??
    toPercentage(rejectedCount, reviewedTotal)
  );

  return {
    total_tasks: toNumber(source?.total_tasks ?? source?.total_tasks_added ?? 0),
    completed_tasks: toNumber(source?.completed_tasks ?? source?.total_tasks_completed ?? 0),
    pending_tasks: toNumber(source?.pending_tasks ?? source?.pending ?? 0),
    active_tasks: toNumber(source?.active_tasks ?? source?.total_tasks_allocated ?? 0),
    quality_accepted_as_is_percentage: acceptedAsIsPercentage,
    quality_accepted_with_edits_percentage: acceptedWithEditsPercentage,
    quality_rejected_percentage: rejectedPercentage,
    quality_accepted_as_is_tasks: acceptedAsIs,
    quality_accepted_with_edits_tasks: acceptedWithEdits,
    quality_accepted_tasks: acceptedTotal,
    quality_rejected_tasks: rejectedCount,
    awaiting_review_tasks: toNumber(source?.awaiting_review_tasks ?? source?.total_reviews_allocated ?? 0),
    not_started_tasks: toNumber(source?.not_started_tasks ?? 0),
    aht: toNumber(source?.aht ?? 0),
    completion_percentage: toNumber(source?.completion_percentage ?? 0),
    stage_name: source?.stage_name,
  };
};

export const getAnalytics = createAsyncThunk(
  'project/qa-analytics/overview/get',
  async (payload: AnalyticsPayload = { association_type: '' }, thunkAPI) => {
    try {
      const {
        search,
        page,
        pageSize,
        sort_by,
        sort_order,
        project_id,
        type,
        association_type,
        stage_id,
        tab,
        start_date,
        end_date,
        granularity,
      } = payload;
      const queryString = new URLSearchParams({
        ...(search && { search }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { page_size: pageSize.toString() }),
        ...(sort_by && { sort_by }),
        ...(sort_order && { sort_order }),
        ...(project_id && { project_id }),
        ...(stage_id && { stage_id: stage_id }),
        ...(start_date && { start_date: start_date }),
        ...(end_date && { end_date: end_date }),
        ...(granularity && { granularity }),
        ...(type && { type }),
        tab: tab || 'overview',
        ...(association_type && { association_type })
      }).toString();
      const response = await api.get(`project/qa-analytics/overview/?${queryString}`);
      const { status, data } = response;
      if (status === 200) {
        const rawData = data?.data ?? data ?? {};
        return {
          message: data?.message || '',
          status: data?.status ?? status,
          data: normalizeOverviewMetrics(rawData),
        };
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        let errorMessage = `Request failed with status ${status}`;
        if (data && data.message) {
          errorMessage += `: ${data.message}`;
          console.log({ errorMessage });
        }
        return thunkAPI.rejectWithValue({ message: errorMessage });
      } else if (error.request) {
        return thunkAPI.rejectWithValue({ message: 'No response received' });
      } else {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
    }
  }
);

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
  },
  extraReducers
});

// export const {} = analyticsSlice.actions;
export default analyticsSlice.reducer;
